import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Person, TeamMemberDetails, TeamMemberRole, TeamMemberWithDetails } from '../sageHomeApi';
import { TeamRoles } from 'src/constants/TeamRoles';

export interface Address {
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    accessDetails?: string;
}

export interface OnboardingState {
    careRecipient?: TeamMemberWithDetails;
    hasAcceptedTerms: boolean;
    hasConfirmedUserInfo: boolean;
    hasConsentedToEmail: boolean;
    hasConsentedToSms?: boolean;
    hasConsentedToEmailMarketing: boolean;
    onboardingUser?: TeamMemberWithDetails;
    teamRole?: TeamMemberRole;
    address?: Address;
}

const initialState: OnboardingState = {
    careRecipient: undefined,
    hasAcceptedTerms: false,
    hasConfirmedUserInfo: false,
    hasConsentedToEmail: false,
    hasConsentedToSms: undefined,
    hasConsentedToEmailMarketing: false,
    onboardingUser: undefined,
    teamRole: undefined,
};

const onboardingSlice = createSlice({
    name: 'onboarding',
    initialState,
    reducers: {
        setCareRecipientDetails(state, action: PayloadAction<Partial<TeamMemberWithDetails>>) {
            state.careRecipient = { ...state.careRecipient, ...action.payload } as Person &
                TeamMemberDetails;
        },
        setHasAcceptedTerms(state, action: PayloadAction<boolean>) {
            state.hasAcceptedTerms = action.payload;
        },
        setHasConfirmedUserInfo(state, action: PayloadAction<boolean>) {
            state.hasConfirmedUserInfo = action.payload;
        },
        setHasConsentedToEmail(state, action: PayloadAction<boolean>) {
            state.hasConsentedToEmail = action.payload;
        },
        setHasConsentedToEmailMarketing(state, action: PayloadAction<boolean>) {
            state.hasConsentedToEmailMarketing = action.payload;
        },
        setHasConsentedToSms(state, action: PayloadAction<boolean>) {
            state.hasConsentedToSms = action.payload;
        },
        setOnboardingUserDetails(state, action: PayloadAction<Partial<TeamMemberWithDetails>>) {
            const payload = action.payload as TeamMemberWithDetails;
            state.onboardingUser = { ...state.onboardingUser, ...payload };

            // If role is provided in payload, set teamRole as well
            if (payload.role) {
                state.teamRole = payload.role;
            }

            // If the role is CARE_RECIPIENT, also set careRecipient
            if (payload.role === TeamRoles.CareRecipient) {
                state.careRecipient = state.onboardingUser;
            }

            if (payload.isTermsAccepted) {
                state.hasAcceptedTerms = payload.isTermsAccepted;
            }
        },
        setTeamRole(state, action: PayloadAction<TeamMemberRole>) {
            state.teamRole = action.payload;
        },
        setAddress(state, action: PayloadAction<Address | null>) {
            state.address = action.payload ?? undefined;
        },
    },
    selectors: {
        selectCareRecipient: (onboardingState: OnboardingState) => onboardingState.careRecipient,
        selectHasAcceptedTerms: (onboardingState: OnboardingState) =>
            onboardingState.hasAcceptedTerms,
        selectHasConfirmedUserInfo: (onboardingState: OnboardingState) =>
            onboardingState.hasConfirmedUserInfo,
        selectHasConsentedToEmail: (onboardingState: OnboardingState) =>
            onboardingState.hasConsentedToEmail,
        selectHasConsentedToEmailMarketing: (onboardingState: OnboardingState) =>
            onboardingState.hasConsentedToEmailMarketing,
        selectOnboardingState: (onboardingState: OnboardingState) => onboardingState,
        selectOnboardingUser: (onboardingState: OnboardingState) => onboardingState.onboardingUser,
        selectTeamRole: (onboardingState: OnboardingState) => onboardingState.teamRole,
        selectAddress: (onboardingState: OnboardingState) => onboardingState.address,
    },
});

export const {
    setCareRecipientDetails,
    setHasAcceptedTerms,
    setHasConfirmedUserInfo,
    setHasConsentedToEmail,
    setHasConsentedToEmailMarketing,
    setHasConsentedToSms,
    setOnboardingUserDetails,
    setTeamRole,
    setAddress,
} = onboardingSlice.actions;

export const {
    selectCareRecipient,
    selectHasAcceptedTerms,
    selectHasConfirmedUserInfo,
    selectHasConsentedToEmail,
    selectHasConsentedToEmailMarketing,
    selectOnboardingState,
    selectOnboardingUser,
    selectTeamRole,
    selectAddress,
} = onboardingSlice.selectors;

export default onboardingSlice.reducer;
